import React, {useState, useEffect, useMemo, useRef } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Grid, TextField, Select, MenuItem, FormControl, InputLabel,
    OutlinedInput, IconButton, Box, Switch, TextareaAutosize
} from "@mui/material";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import PropTypes from "prop-types";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MDButton from "components/MDButton";
import FooterActions from "../control-config/components/tabcomponents/footer/footeractions";
import MDTypography from "../../components/MDTypography";
import MDBox from "../../components/MDBox";
import Endpoints from "../utils/endpoints";
import toast from "react-hot-toast";
import useConfigApi from "../utils/configapi";

const GenericForm = ({
    open = false,
    handleClose,
    title,
    fields,
    validationSchema,
    onSubmit,
    initialData,
    mode = "create",
    useDialog = true,
    onViewOutputs,
    onRevertAll,
    onLocalAll,
    onValidate,
    onClose,
    onDelete,
    maxWidth = "lg",
    CustomComponent = null,
    onWatchChange // <-- callback for value changes
}) => {
    const [showPassword, setShowPassword] = useState({});
    const [dynamicFieldOptions, setDynamicFieldOptions] = useState({});
    const [dynamicGroupId, setDynamicGroupId] = useState("");
    const [fileKeys, setFileKeys] = useState({});
    // const firstFieldRef = useRef(null);

    // useEffect(() => {
    //   if (open && firstFieldRef.current && mode !== "view") {
    //     setTimeout(() => {
    //       firstFieldRef.current.focus();
    //     }, 300);
    //   }
    // }, [open, mode]);

    // const defaultValues = initialData
    //     ? Object.fromEntries(fields.map(f => [f.name, initialData?.[f.name] || f.default || ""]))
    //     : Object.fromEntries(fields.map(f => [f.name, f.default || ""]));
    const defaultValues = useMemo(() => {
        const dv = Object.fromEntries(
            //fields.map(f => [f.name, initialData?.[f.name] ?? f.default ?? ""])
            fields.map(f => {
                let val = initialData?.[f.name] ?? f.default ?? "";

                // if (f.type === "select" && f.multiple && typeof val === "string") {
                //     val = val.split(",").map(s => s.trim()); // Convert CSV to array
                // }
                // console.log(val);

                return [f.name, val];
            })
        );
        return dv;
    }, [initialData, fields]);
    // const defaultValues = useMemo(() => {
    //     return Object.fromEntries(
    //         fields.map(f => [f.name, initialData?.[f.name] ?? f.default ?? ""])
    //     );
    // }, [initialData, fields]);
    // console.log("defaultValues", defaultValues);
    const methods = useForm({
        defaultValues,
        resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    });


    const { handleSubmit, control, reset, getValues, watch, setValue } = methods;

    // Notify parent on value changes
    useEffect(() => {
        if (typeof onWatchChange === 'function') {
            const subscription = watch((values, info) => {
                onWatchChange(values, info, setValue);
            });
            return () => subscription.unsubscribe();
        }
    }, [onWatchChange, watch]);

    const watchedValues = watch();

    // const setFieldOptions = (fieldName, options) => {
    //     setDynamicFieldOptions(prev => ({
    //         ...prev,
    //         [fieldName]: options,
    //     }));
    // };
    const api = useConfigApi();

    const fieldDependencies = [
        {
            watchField: "controlType",
            mapFn: async (value, fields) => {
                console.log(fields);
                const selected = fields.find(f => f.name === "controlType")?.options?.find(opt => opt.value === value);
                setValue("controlId", selected?.value || "");
            }
        },
        {
            watchField: "grpConfigName",
            mapFn: async (value, fields) => {
                try {
                    if(mode === 'create') {
                        const response  = await api.get(Endpoints.groups.generateGroupId(value));
                        setValue("grpId", response?.data?.data || "");
                    }

                } catch (e) {
                    setDynamicGroupId("");
                    console.log(e);
                }
            }
        }

    // Add more dependencies here as needed
    ];


    // useEffect(() => {
    //     fieldDependencies.forEach(dep => {
    //         const value = watch(dep.watchField);
    //         const newValues = dep.mapFn(value, fields);
    //         Object.entries(newValues).forEach(([key, val]) => setValue(key, val));
    //     });
    // }, fieldDependencies.map(dep => watch(dep.watchField)));
    // useEffect(() => {
    //     let isMounted = true;
    //
    //     fieldDependencies.forEach(async (dep) => {
    //         const value = watch(dep.watchField);
    //         console.log(`watchField: ${dep.watchField}, value:`, value);  // <-- Add this
    //
    //         if (!isMounted) return;
    //
    //         const newValues = await dep.mapFn(value, fields);
    //         if (isMounted && newValues) {
    //             Object.entries(newValues).forEach(([key, val]) => setValue(key, val));
    //         }
    //     });
    //
    //     return () => {
    //         isMounted = false;
    //     };
    // }, fieldDependencies.map(dep => watch(dep.watchField)));

    useEffect(() => {
        if (initialData) {
            console.log("initialData", initialData);
            reset({...defaultValues, ...initialData} );
            // const grpDep = fieldDependencies.find(dep => dep.watchField === "grpId");
            // if (grpDep) {
            //     grpDep.mapFn(initialData.grpId, fields);  // 👈 this ensures options are fetched and set
            // }
        }
    }, [initialData]);

    const submitHandler = (data) => {
        // Convert numeric fields to actual numbers
        const transformedData = { ...data };
        fields.forEach(field => {
            const val = data[field.name];
            if (field.type === 'number') {
                transformedData[field.name] = val !== "" ? Number(val) : null;
            } else if (field.type === 'file' && field.name === 'reciptLogo') {
              // If user hit "Clear Logo", we store "" in RHF; send null to API to delete
              transformedData.reciptLogo = val ? val : null;
            } else if (field.multiple && Array.isArray(val)) {
                transformedData[field.name] = val.join(","); // Convert array to CSV string
            }
        });
        if (mode === "edit" || mode === "create") {
            const result = onSubmit(transformedData);
            if (result === false) return;
            reset();
            if (useDialog) handleClose();
        }
    };

    const handleValidate = () => {
        const formData = getValues();
        const transformedData = { ...formData };
        fields.forEach(field => {
            const val = formData[field.name];
            if (field.type === 'number') {
                transformedData[field.name] = val !== "" ? Number(val) : null;
            }
        });
        if (onValidate) onValidate(transformedData);
    };

    const togglePasswordVisibility = (fieldName) => {
        setShowPassword((prev) => ({
            ...prev,
            [fieldName]: !prev[fieldName],
        }));
    };

    
    const renderFields = () => (
      <Grid container spacing={2}>
        {fields.map(({ name, label, type = "text",
            options = [], xs = 3, readonly = false,
            placeholder, maxLength, format,
            hidden, multiple, accept,
          }) => {
            if (hidden || (mode === "create" && name === "slNo")) return null;

            return (
              <Grid item xs={xs} key={name}>
                <FormControl fullWidth variant="outlined">
                  {type === "select" && multiple ? (
                    <>
                      <InputLabel id={`${name}-label`}>{label}</InputLabel>
                      <Controller
                        name={name}
                        control={control}
                        render={({ field: { value, onChange, onBlur, ...restField } }) => (
                          <Select
                            {...restField}
                            multiple
                            labelId={`${name}-label`}
                            input={<OutlinedInput label={label} />}
                            value={
                              Array.isArray(value)
                                ? value
                                : typeof value === "string" && value.trim() !== ""
                                ? value
                                    .split(",")
                                    .map((v) =>
                                      /^\d+$/.test(v.trim()) ? parseInt(v.trim(), 10) : v.trim()
                                    )
                                : []
                            }
                            onChange={(e) => {
                              const selectedValues = e.target.value;
                              onChange?.(selectedValues);
                              const dep = fieldDependencies.find((dep) => dep.watchField === name);
                              if (dep) dep.mapFn(selectedValues, fields);
                            }}
                            renderValue={(selected) => {
                              const selectedLabels = selected
                                .map((val) => {
                                  const option = options.find(
                                    (opt) => String(opt.value) == String(val)
                                  );
                                  // const option = options.find((opt) => opt.value == val);

                                  return option ? option.label : null;
                                })
                                .filter(Boolean);
                              return selectedLabels.join(", ");
                            }}
                            IconComponent={() => (
                              <ArrowDropDownIcon
                                sx={{ color: "black", fontSize: "25px !important" }}
                              />
                            )}
                            disabled={mode === "view" || readonly}
                            sx={{
                              height: "45px",
                              "& .MuiSelect-select": {
                                paddingTop: "14px",
                                paddingBottom: "14px",
                                display: "block",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              },
                            }}
                          >
                            {(options || []).map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </>
                  ) : type === "select" ? (
                    <>
                      <InputLabel id={`${name}-label`}>{label}</InputLabel>
                      <Controller
                        name={name}
                        control={control}
                        render={({ field: { value, onChange, onBlur, ...restField } }) => (
                          <Select
                            {...restField}
                            labelId={`${name}-label`}
                            input={<OutlinedInput label={label} />}
                            // value={value ?? ""}
                            value={options.some((opt) => String(opt.value) === String(value)) ? value : ""}
                            label={label}
                            fullWidth
                            size="medium"
                            onChange={(e) => {
                              onChange?.(e);
                              const dep = fieldDependencies.find((dep) => dep.watchField === name);
                              if (dep) dep.mapFn(e.target.value, fields);
                            }}
                            IconComponent={() => (
                              <ArrowDropDownIcon
                                sx={{ color: "black", fontSize: "25px !important" }}
                              />
                            )}
                            disabled={mode === "view" || readonly}
                            sx={{
                              height: "45px",
                              "& .MuiSelect-select": {
                                paddingTop: "14px",
                                paddingBottom: "14px",
                                display: "block",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              },
                            }}
                          >
                            {/*<MenuItem value="">*/}
                            {/*    <em>{placeholder || `Select ${label}`}</em>*/}
                            {/*</MenuItem>*/}
                            {(options || []).map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                            {/*{(dynamicFieldOptions[name] || options).map((opt) => (*/}
                            {/*    <MenuItem key={opt.value} value={opt.value}>*/}
                            {/*        {opt.label}*/}
                            {/*    </MenuItem>*/}
                            {/*))}*/}
                          </Select>
                        )}
                      />
                    </>
                  ) : type === "switch" ? (
                    <>
                      <MDTypography sx={{ mb: 1, fontSize: "0.9rem", color: "rgba(0, 0, 0, 0.6)" }}>
                        {label}
                      </MDTypography>
                      <Controller
                        name={name}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Switch
                            checked={!!value}
                            onChange={(e) => onChange(e.target.checked)}
                            disabled={mode === "view" || readonly}
                          />
                        )}
                      />
                    </>
                  ) : type === "file" ? (
                    // <>
                    //   <MDTypography sx={{ mb: 1, fontSize: "0.9rem", color: "rgba(0, 0, 0, 0.6)" }}>
                    //     {label}
                    //   </MDTypography>

                    //   <Controller
                    //     name={name}
                    //     control={control}
                    //     render={({ field: { value, onChange } }) => {
                    //       const handleFile = async (e) => {
                    //         const file = e.target.files?.[0];
                    //         if (!file) return;

                    //         // validate type & size
                    //         if (!/image\/(png|jpeg)/i.test(file.type)) {
                    //           toast.error("Only PNG or JPEG allowed for receipt logo.");
                    //           return;
                    //         }
                    //         if (file.size > 200 * 1024) {
                    //           toast.error("Receipt logo too large. Max 200KB.");
                    //           return;
                    //         }

                    //         // read as base64 data URL
                    //         const toDataUrl = (f) =>
                    //           new Promise((resolve, reject) => {
                    //             const reader = new FileReader();
                    //             reader.onload = () => resolve(reader.result);
                    //             reader.onerror = reject;
                    //             reader.readAsDataURL(f);
                    //           });

                    //         try {
                    //           const dataUrl = await toDataUrl(file); // e.g. data:image/png;base64,....
                    //           onChange(String(dataUrl)); // store STRING, not File
                    //         } catch {
                    //           toast.error("Failed to read image.");
                    //         }
                    //       };

                    //       const previewSrc =
                    //         typeof value === "string" && value.startsWith("data:image/")
                    //           ? value
                    //           : "";

                    //       return (
                    //         <>
                    //           <input
                    //             type="file"
                    //             accept={accept || "image/png,image/jpeg"}
                    //             onChange={handleFile}
                    //             disabled={mode === "view" || readonly}
                    //           />

                    //           {previewSrc && (
                    //             <Box sx={{ mt: 1 }}>
                    //               <img
                    //                 src={previewSrc}
                    //                 alt="Receipt Logo Preview"
                    //                 style={{ height: 40, borderRadius: 4, objectFit: "contain" }}
                    //               />
                    //             </Box>
                    //           )}

                    //           {previewSrc && mode !== "view" && !readonly && (
                    //             <MDButton
                    //               size="small"
                    //               color="light"
                    //               sx={{ mt: 1 }}
                    //               onClick={() => onChange("")}
                    //             >
                    //               Clear Logo
                    //             </MDButton>
                    //           )}
                    //         </>
                    //       );
                    //     }}
                    //   />
                    // </>
                    <>
                      <MDTypography sx={{ mb: 1, fontSize: "0.9rem", color: "rgba(0, 0, 0, 0.6)" }}>
                        {label}
                      </MDTypography>

                      <Controller
                        name={name}
                        control={control}
                        render={({ field: { value, onChange } }) => {
                          const fileKey = fileKeys[name] || 0;

                          const handleFile = async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;

                            // validate type & size
                            if (!/image\/(png|jpeg)/i.test(file.type)) {
                              toast.error("Only PNG or JPEG allowed for receipt logo.");
                              return;
                            }
                            if (file.size > 1024 * 1024) {
                              toast.error("Receipt logo too large. Max 1MB.");
                              return;
                            }

                            // read as base64 data URL
                            const toDataUrl = (f) =>
                              new Promise((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = () => resolve(reader.result);
                                reader.onerror = reject;
                                reader.readAsDataURL(f);
                              });

                            try {
                              const dataUrl = await toDataUrl(file); // "data:image/...;base64,...."
                              onChange(String(dataUrl)); // store string in form state
                            } catch {
                              toast.error("Failed to read image.");
                            }
                          };

                          const clearLogo = () => {
                            onChange("");
                            setValue(name, ""); // also reset RHF value
                            setFileKeys((prev) => ({ ...prev, [name]: (prev[name] || 0) + 1 })); // remount <input>
                          };

                          const previewSrc =
                            typeof value === "string" && value.startsWith("data:image/")
                              ? value
                              : "";

                          return (
                            <>
                              <input
                                key={`${name}-${fileKey}`} // remounts on clear
                                type="file"
                                accept={accept || "image/png,image/jpeg"}
                                onClick={(e) => {
                                  e.currentTarget.value = "";
                                }} // allow same-file reselect
                                onChange={handleFile}
                                disabled={mode === "view" || readonly}
                              />

                              {previewSrc && (
                                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                  <img
                                    src={previewSrc}
                                    alt="Receipt Logo Preview"
                                    style={{ height: 40, borderRadius: 4, objectFit: "contain" }}
                                  />
                                  {mode !== "view" && !readonly && (
                                    <MDButton size="small" color="light" onClick={clearLogo}>
                                      Clear Logo
                                    </MDButton>
                                  )}
                                </Box>
                              )}
                            </>
                          );
                        }}
                      />
                    </>
                  ) : type === "textarea" ? (
                    <Controller
                      name={name}
                      control={control}
                      render={({
                        field: { value, onChange, onBlur, ...restField },
                        fieldState: { error },
                      }) => (
                        <>
                          <MDTypography
                            sx={{ mb: 1, fontSize: "0.9rem", color: "rgba(0, 0, 0, 0.6)" }}
                          >
                            {label}
                          </MDTypography>
                          <TextField
                            {...restField}
                            // inputRef={index === fields.findIndex(f => !f.hidden && !f.readonly) ? firstFieldRef : null}
                            // value={value ?? ""}
                            value={
                              options.some((opt) => String(opt.value) === String(value))
                                ? value
                                : ""
                            }
                            onChange={(e) => {
                              const trimmed = maxLength
                                ? e.target.value.slice(0, maxLength)
                                : e.target.value;
                              onChange(trimmed);
                            }}
                            onBlur={onBlur}
                            multiline
                            rows={4}
                            placeholder={placeholder}
                            fullWidth
                            error={!!error}
                            helperText={error ? error.message : ""}
                            InputProps={{
                              readOnly: mode === "view" || readonly,
                            }}
                            inputProps={{
                              ...(maxLength ? { maxLength } : {}),
                            }}
                          />
                        </>
                      )}
                    />
                  ) : (
                    <Controller
                      name={name}
                      control={control}
                      render={({
                        field: { value, onChange, onBlur, ...restField },
                        fieldState: { error },
                      }) => {
                        // const handleChange = (e) => {
                        //   let raw = e.target.value.replace(/\D/g, ""); // only digits
                        //   if (maxLength && raw.length > maxLength)
                        //     raw = raw.slice(0, maxLength);

                        //   if (format === "amount" && type === "number") {
                        //     // Format raw as amount (last 2 digits = decimal)
                        //     const formatted =
                        //       raw.length <= 2
                        //         ? `0.${raw.padStart(2, "0")}`
                        //         : `${parseInt(raw.slice(0, -2), 10)}.${raw.slice(-2)}`;

                        //     onChange(formatted); // store formatted string
                        //   } else if (type === "number") {
                        //     if (raw === "" || /^[0-9]*$/.test(raw)) {
                        //       onChange(raw);
                        //     }
                        //   } else {
                        //     onChange(e); // default handling
                        //   }
                        // };
                        const handleChange = (e) => {
                          const val = e.target.value;
                          if (format === "amount" && type === "number") {
                            let raw = val.replace(/\D/g, "");
                            if (maxLength && raw.length > maxLength) raw = raw.slice(0, maxLength);
                            const formatted =
                              raw.length <= 2
                                ? `0.${raw.padStart(2, "0")}`
                                : `${parseInt(raw.slice(0, -2), 10)}.${raw.slice(-2)}`;
                            onChange(formatted);
                          } else if (type === "number") {
                            let raw = val.replace(/\D/g, "");
                            if (maxLength && raw.length > maxLength) raw = raw.slice(0, maxLength);
                            if (raw === "" || /^[0-9]*$/.test(raw)) onChange(raw);
                          } else {
                            const trimmed = maxLength ? val.slice(0, maxLength) : val;
                            onChange(trimmed);
                          }
                        };
                        const formatWithCommas = (numStr) => {
                          if (!numStr) return "";

                          const num = Number(numStr);
                          if (isNaN(num)) return numStr;

                          return num.toLocaleString("en-IN"); // 🇮🇳 Indian format
                        };

                        const displayValue = (() => {
                          if (format === "amount") {
                            if (!value) return "";
                            const [whole, decimal = ""] = value.toString().split(".");
                            const formattedWhole = formatWithCommas(whole);
                            const formattedDecimal = decimal.padEnd(2, "0").slice(0, 2);
                            return `${formattedWhole}.${formattedDecimal}`;
                          }
                          return value ?? "";
                        })();

                        return (
                          <TextField
                            {...restField}
                            // inputRef={index === fields.findIndex(f => !f.hidden && !f.readonly) ? firstFieldRef : null}
                            value={displayValue}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            onBlur={(e) => {
                              onBlur?.(e);
                              const dep = fieldDependencies.find((dep) => dep.watchField === name);
                              if (dep) dep.mapFn(e.target.value, fields);
                            }}
                            type={type === "password" && !showPassword[name] ? "password" : "text"}
                            inputProps={{
                              ...(type === "number" ? { inputMode: "numeric" } : {}),
                              ...(maxLength ? { maxLength } : {}),
                            }}
                            label={label}
                            variant="outlined"
                            placeholder={placeholder}
                            fullWidth
                            error={!!error}
                            helperText={error ? error.message : ""}
                            InputProps={{
                              readOnly: mode === "view" || readonly,
                              endAdornment: type === "password" && (
                                <IconButton onClick={() => togglePasswordVisibility(name)}>
                                  {showPassword[name] ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              ),
                            }}
                          />
                        );
                      }}
                    />
                  )}
                </FormControl>
              </Grid>
            );
          }
        )}
      </Grid>
    );


    const displayName =
      methods.getValues()?.label ||
      methods.getValues()?.hostName ||
      methods.getValues()?.terminalId ||
      methods.getValues()?.slNo ||
      "this item";
    const formContent = (
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitHandler)} noValidate>
          <MDBox sx={{ p: 3 }}>
            {mode === "delete" ? (
              <MDTypography fontSize="1rem">
                  Are you sure you want to delete <strong> {displayName} </strong>?
              </MDTypography>
            ) : (
              <>
                {renderFields()}
                {CustomComponent && (
                  <Box sx={{ mt: 3 }}>
                    <CustomComponent selectedValues={watchedValues} />
                  </Box>
                )}
              </>
            )}
          </MDBox>

          {useDialog ? (
            <DialogActions>
              {mode === "view" ? (
                <MDButton onClick={handleClose} color="light">
                  Cancel
                </MDButton>
              ) : mode === "delete" ? (
                <>
                  <MDButton onClick={handleClose} color="light">
                    Cancel
                  </MDButton>
                  <MDButton
                    type="button"
                    onClick={() => {
                      onDelete?.(getValues());
                      handleClose?.();
                    }}
                    color="error"
                  >
                    Yes
                  </MDButton>
                </>
              ) : (
                <>
                  <MDButton type="submit" variant="gradient" color="info">
                    Submit
                  </MDButton>
                  <MDButton onClick={handleClose} color="light">
                    Cancel
                  </MDButton>
                </>
              )}
            </DialogActions>
          ) : (
            <FooterActions
              onRevertAll={() => {
                reset(); // instantly clear all fields
                if (onRevertAll) onRevertAll(); // optional callback to parent
              }}
              onLocalAll={onLocalAll}
              onValidate={handleValidate}
              onClose={onClose}
              onViewOutputs={onViewOutputs}
            />
          )}
        </form>
      </FormProvider>
    );

    return useDialog ? (
        <Dialog open={open} onClose={handleClose} maxWidth={maxWidth} PaperProps={{ sx: { borderRadius: 4 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", pr: 2 }}>{title}</DialogTitle>
            <DialogContent dividers>{formContent}</DialogContent>
        </Dialog>
    ) : (
        <Box sx={{ borderRadius: 4, bgcolor: "#fff" }}>{formContent}</Box>
    );
};

GenericForm.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    title: PropTypes.string.isRequired,
    fields: PropTypes.array.isRequired,
    validationSchema: PropTypes.object,
    onSubmit: PropTypes.func,
    initialData: PropTypes.object,
    mode: PropTypes.oneOf(["create", "edit", "view", "delete"]),
    useDialog: PropTypes.bool,
    onViewOutputs: PropTypes.func,
    onRevertAll: PropTypes.func,
    onLocalAll: PropTypes.func,
    onValidate: PropTypes.func,
    onClose: PropTypes.func,
    onDelete: PropTypes.func,
    maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
    CustomComponent: PropTypes.elementType,
    watch: PropTypes.func,
    onWatchChange: PropTypes.func,
};

export default GenericForm;
